/* ==========================================================================
   Young Grandpa - size guide modal: slide-up state, right-column anchor,
   IN/CM unit toggle
   --------------------------------------------------------------------------
   The bf-size-charts app builds .scr-modal into the DOM on first open and
   reuses it after. It animates the card itself purely by toggling inline
   opacity/visibility/transform on that one element - no class, no display
   swap - so assets/yg-custom.css has nothing to key a slide-up transform off
   of. This mirrors the app's own inline visibility onto a real class
   (.yg-size-guide--open) so the CSS can override the resting transform per
   state while the app's own inline transition animates the change.

   Desktop position: the app centers .scr-modal on the viewport by default
   (left: 50% + translateX(-50%) in its own stylesheet). Rather than fight
   that transform, this measures the real right-column buy-box element
   (same one assets/yg-custom.css drives into grid-column: 3) each time the
   modal opens, and writes its bounds into --yg-sg-* custom properties that
   assets/yg-custom.css reads with `right`/`top`/`width` - independent of
   however the app centers its own overlay.

   Unit toggle: the app has no IN/CM control and no per-cell unit data of
   its own. This reads whatever inch value the app already rendered for
   THIS product (never hardcoded) into data-in, derives data-cm at 2.54x,
   and injects a toggle above the table - the same data-in/data-cm pattern
   as .design-refs/size-guide-demo.html.

   .scr-modal doesn't exist until the app first builds it (possibly well
   after DOMContentLoaded - it also waits for its own button to scroll into
   view first), so this watches the whole document for it rather than
   querying once.
   ========================================================================== */

(function () {
  'use strict';

  var OPEN_CLASS = 'yg-size-guide--open';
  var COLUMN_SELECTOR =
    '.product-information .product-details > .group-block > .group-block-content > .group-block:has(product-form-component)';
  var DESKTOP_QUERY = '(min-width: 750px)';
  /* A comfortable width for the card's own content (title, tip bar, 6-column
     table, footnote) at the app's native 16px table font - roughly matching
     the reference demo's own 600px modal once its extra chrome is
     accounted for. NOT the right column's own width: that column measured
     as narrow as ~408px in testing, which produced a card too cramped for
     its content (both a horizontal AND vertical scrollbar inside it) - the
     column is only used below to anchor where the card's right edge sits,
     never to size it. */
  var TARGET_WIDTH = 480;
  /* The card no longer has one right width: the app's chart data on this
     store runs from 3 columns to 7, in either orientation, so the card is
     sized to whatever the table's own nowrap content actually needs,
     between these two bounds. Below MIN_WIDTH the title and tip banner get
     cramped for no gain; above MAX_WIDTH the card stops reading as anchored
     to the buy box and starts covering the product image. */
  var MIN_WIDTH = 440;
  var MAX_WIDTH = 620;
  /* Everything between the card's edge and the table: 14px of card padding
     and 22px of .scr.basic padding on each side, plus room for that box's
     vertical scrollbar, which appears on the taller charts. The 72 is the
     same total as when the card held all 36 itself, so the width this picks
     is unchanged by that split - only the scrollbar's lane moved. */
  var CARD_CHROME = 2 * (14 + 22) + 20;
  /* Generous estimate of the card's own full height (title + tip banner +
     unit toggle + up to ~6 table rows + footnote + the card's own 34px/30px
     top/bottom padding) - used to decide how far up to pull the anchor
     point on shorter viewports. Was a flat 280px floor on .scr.basic alone,
     which didn't account for the card's own padding on top of it and
     wasn't sized to the content at all - the table was getting cut off
     with both a scrollbar AND unused space below it, since the card was
     anchored at the add-to-cart button's exact position regardless of how
     little room that left underneath. */
  var CONTENT_HEIGHT = 600;
  /* Matches .scr-modal's own `padding: 34px 36px 30px` in yg-custom.css -
     .scr.basic's max-height needs to leave this much room inside the card
     for the card's own top/bottom padding, or the two together could push
     the card's total height past what CONTENT_HEIGHT actually reserved. */
  var MODAL_VERTICAL_PADDING = 64;

  var desktop = window.matchMedia(DESKTOP_QUERY);
  var attached = null;
  var wasOpen = false;
  var resizeTimer = null;
  var scrollTimer = null;

  function isOpen(modal) {
    return modal.style.visibility !== 'hidden' && modal.style.opacity !== '0';
  }

  function headerHeight() {
    var value = getComputedStyle(document.body).getPropertyValue('--header-height');
    var parsed = parseFloat(value);
    return isNaN(parsed) ? 80 : parsed;
  }

  /**
   * Widest the table can ever want to be: its own max-content width with
   * every convertible cell showing whichever of its in/cm values is longer.
   * Measured off the worst case so switching units never resizes the card.
   * @param {HTMLElement} modal
   * @returns {number}
   */
  function naturalTableWidth(modal) {
    var table = modal.querySelector('.scr-table');
    if (!table) return 0;

    modal.style.setProperty('--yg-sg-fit', '1');

    var restore = [];
    table.querySelectorAll('td[data-cm]').forEach(function (cell) {
      var widest = cell.getAttribute('data-cm');
      if (!widest || widest.length <= cell.textContent.length) return;
      restore.push([cell, cell.textContent]);
      cell.textContent = widest;
    });

    modal.classList.add('yg-sg-measure');
    var width = table.getBoundingClientRect().width;
    modal.classList.remove('yg-sg-measure');

    restore.forEach(function (pair) {
      pair[0].textContent = pair[1];
    });

    return width;
  }

  /**
   * Measures the real right-column buy-box and writes its bounds onto the
   * modal as custom properties, so CSS can anchor there instead of wherever
   * the app centers itself.
   * @param {HTMLElement} modal
   */
  function position(modal) {
    if (!desktop.matches) return;

    var column = document.querySelector(COLUMN_SELECTOR);
    if (!column) return;

    var rect = column.getBoundingClientRect();
    if (!rect.width) return;

    var addToCart = column.querySelector('button[name="add"]');
    var anchorRect = addToCart ? addToCart.getBoundingClientRect() : rect;

    var margin = 16;
    var minTop = headerHeight() + 16;
    /* Prefer anchoring right at the add-to-cart button, but never let that
       leave less than CONTENT_HEIGHT of room below it - pull the anchor UP
       instead of compressing the card when the button sits low on a
       shorter viewport. This is what actually fixes the cut-off table: the
       card no longer settles for whatever space happened to be left under
       the button, it claims the space it needs first. */
    var idealTop = Math.min(anchorRect.top, window.innerHeight - CONTENT_HEIGHT - margin);
    var top = Math.max(minTop, idealTop);

    var right = Math.max(16, window.innerWidth - rect.right);
    var room = Math.min(MAX_WIDTH, window.innerWidth - right - 16);
    var wanted = naturalTableWidth(modal);
    var width = Math.min(room, Math.max(MIN_WIDTH, wanted ? wanted + CARD_CHROME : TARGET_WIDTH));
    /* The header sits above .scr.basic inside the card, so the band it
       occupies comes out of what the scroller is allowed to claim. */
    var maxHeight = Math.max(
      240,
      window.innerHeight - top - margin - MODAL_VERTICAL_PADDING - headerBand(modal)
    );

    modal.style.setProperty('--yg-sg-top', top + 'px');
    modal.style.setProperty('--yg-sg-right', right + 'px');
    modal.style.setProperty('--yg-sg-width', width + 'px');
    modal.style.setProperty('--yg-sg-max-h', maxHeight + 'px');
  }

  /* =========================================================
     TABLE FIT - nowrap columns, scaled type, scroll fallback
     ========================================================= */

  /**
   * Moves the app's table into a scroll frame so a table too wide for the
   * card scrolls on its own axis instead of being clipped by .scr.basic.
   * @param {HTMLElement} modal
   * @returns {HTMLElement|null}
   */
  function wrapTable(modal) {
    var table = modal.querySelector('.scr-table');
    if (!table) return null;

    var viewport = table.parentNode;
    if (viewport && viewport.classList && viewport.classList.contains('yg-sg-table-viewport')) {
      return viewport.parentNode;
    }

    var frame = document.createElement('div');
    frame.className = 'yg-sg-table-scroll';
    viewport = document.createElement('div');
    viewport.className = 'yg-sg-table-viewport';

    table.parentNode.insertBefore(frame, table);
    frame.appendChild(viewport);
    viewport.appendChild(table);

    viewport.addEventListener(
      'scroll',
      function () {
        markOverflow(frame);
      },
      { passive: true }
    );

    return frame;
  }

  /* =========================================================
     HEADER - title lifted clear of the scroll boundary
     ========================================================= */

  /**
   * The close button is absolute against the card while the title sits inside
   * .scr.basic, so content scrolled up under the button - the unit toggle was
   * disappearing behind it. Reserving space inside the scroller can't fix
   * that: a scroller's own padding-top scrolls away with the content. The
   * only thing that stops content reaching the button is starting the
   * scroller below it, which is what the reference demo does by keeping the
   * title in a .modal-head above .modal-scroll. This lifts the app's title
   * into that same fixed header.
   *
   * The title and its 20px margin already occupied this exact space as the
   * scroller's first child, so nothing moves - the scroll boundary just
   * drops below the button.
   * @param {HTMLElement} modal
   */
  function buildHeader(modal) {
    var panel = modal.querySelector('.scr.basic');
    if (!panel) return;

    var header = modal.querySelector('.yg-sg-header');
    var title = panel.querySelector('h2');

    if (title) {
      if (!header) {
        header = document.createElement('div');
        header.className = 'yg-sg-header';
        modal.insertBefore(header, panel);
      }
      while (header.firstChild) header.removeChild(header.firstChild);
      header.appendChild(title);
    }

    if (header) {
      modal.style.setProperty('--yg-sg-head-h', headerBand(modal) + 'px');
    }
  }

  /**
   * How much vertical room the header takes out of the card - its own height
   * plus the margin that separates it from the scroller. Both halves have to
   * be counted or the card ends up that margin taller than the viewport
   * allows. Not named headerHeight: that is already the site-header helper.
   * @param {HTMLElement} modal
   * @returns {number}
   */
  function headerBand(modal) {
    var bar = modal.querySelector('.yg-sg-header');
    if (!bar) return 0;

    var gap = parseFloat(getComputedStyle(bar).marginBottom);
    return bar.getBoundingClientRect().height + (isNaN(gap) ? 0 : gap);
  }

  /* =========================================================
     SCROLL STATE - bottom fade and the fading scrollbar
     ========================================================= */

  /**
   * @param {HTMLElement} modal
   */
  function updateScrollFade(modal) {
    /* One .scr panel per chart: no chart on this store has a parentSizeChart
       today, but the app appends a panel per child chart when one does. */
    modal.querySelectorAll('.scr.basic').forEach(function (panel) {
      var slack = panel.scrollHeight - panel.clientHeight;
      panel.classList.toggle('has-more-below', slack > 1 && panel.scrollTop < slack - 1);
    });
  }

  /**
   * @param {HTMLElement} modal
   */
  function bindScroll(modal) {
    modal.querySelectorAll('.scr.basic').forEach(function (panel) {
      if (panel.hasAttribute('data-yg-scroll')) return;
      panel.setAttribute('data-yg-scroll', '');

      panel.addEventListener(
        'scroll',
        function () {
          updateScrollFade(modal);
          panel.classList.add('is-scrolling');
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(function () {
            panel.classList.remove('is-scrolling');
          }, 700);
        },
        { passive: true }
      );

      /* The how-to-measure diagram in .bottom-description is usually still
         loading on first open, so the height the fade is measured against is
         not final until it lands. */
      panel.querySelectorAll('img').forEach(function (image) {
        if (image.complete) return;
        image.addEventListener('load', function () {
          updateScrollFade(modal);
        }, { once: true });
      });
    });
  }

  /**
   * @param {HTMLElement} frame
   */
  function markOverflow(frame) {
    var viewport = frame.querySelector('.yg-sg-table-viewport');
    if (!viewport) return;

    var slack = viewport.scrollWidth - viewport.clientWidth;
    frame.classList.toggle('is-overflowing', slack > 1);
    frame.classList.toggle('is-at-end', viewport.scrollLeft >= slack - 1);
  }

  /**
   * Scales the table's type and cell padding down until its nowrap content
   * fits the card, stopping at --yg-sg-fit-min so nothing drops below a
   * legible size. Whatever still doesn't fit at that floor is left to the
   * scroll frame.
   * @param {HTMLElement} modal
   */
  function fitTable(modal) {
    var frame = modal.querySelector('.yg-sg-table-scroll');
    var viewport = modal.querySelector('.yg-sg-table-viewport');
    if (!frame || !viewport) return;

    modal.style.setProperty('--yg-sg-fit', '1');

    var available = viewport.clientWidth;
    var wanted = viewport.scrollWidth;
    if (!available || !wanted) return;

    if (wanted > available) {
      var floor = parseFloat(getComputedStyle(modal).getPropertyValue('--yg-sg-fit-min')) || 0.78;
      var scale = Math.max(floor, available / wanted);
      modal.style.setProperty('--yg-sg-fit', String(scale));

      /* Glyph widths don't scale perfectly linearly with font-size, so one
         correction pass off the real measurement settles the rounding. */
      if (scale > floor && viewport.scrollWidth > viewport.clientWidth) {
        scale = Math.max(floor, scale * (viewport.clientWidth / viewport.scrollWidth));
        modal.style.setProperty('--yg-sg-fit', String(scale));
      }
    }

    markOverflow(frame);
  }

  /* =========================================================
     TIP BANNER - flatten merchant rich text into plain <p>
     ========================================================= */

  /**
   * descriptionTop is authored in the app's own rich-text editor, so its top
   * level is not reliably <p>. Found live: JERSEY SIZE GUIDE is a
   * whitespace-only <p> followed by a <blockquote>, and SWEATSHIRT SIZE CHART
   * is a whitespace-only <p> followed by the real paragraph. Only <p> gets the
   * pill in assets/yg-custom.css, so the blockquote rendered outside it in the
   * app's own dark inline colour while the empty <p> rendered as a pill with
   * nothing in it. Flattening every top-level node to a plain <p> and dropping
   * the blank ones puts whatever the merchant wrote inside one pill, whichever
   * element they happened to use.
   * @param {HTMLElement} modal
   */
  function normaliseTip(modal) {
    var tip = modal.querySelector('.top-description');
    if (!tip || tip.hasAttribute('data-yg-tip')) return;
    tip.setAttribute('data-yg-tip', 'ready');

    Array.prototype.slice.call(tip.childNodes).forEach(function (node) {
      if (node.nodeType === 3) {
        if (!node.textContent.trim()) {
          tip.removeChild(node);
          return;
        }
        var wrap = document.createElement('p');
        tip.replaceChild(wrap, node);
        wrap.appendChild(node);
        return;
      }

      if (node.nodeType !== 1 || node.tagName === 'P') return;

      /* Unwrap one level rather than nesting the original element inside the
         new <p> - a <blockquote>/<div> carries the merchant's own inline
         margins and colours, and those are exactly what put the text outside
         the pill's look in the first place. */
      var para = document.createElement('p');
      while (node.firstChild) para.appendChild(node.firstChild);
      tip.replaceChild(para, node);
    });

    Array.prototype.slice.call(tip.children).forEach(function (child) {
      if (child.tagName !== 'P') return;
      if (!child.textContent.trim() && !child.querySelector('img')) tip.removeChild(child);
    });

    if (!tip.textContent.trim() && !tip.querySelector('img')) {
      tip.setAttribute('data-yg-tip', 'empty');
    }
  }

  function layout(modal) {
    normaliseTip(modal);
    buildHeader(modal);
    buildUnitToggle(modal);
    wrapTable(modal);
    bindScroll(modal);
    position(modal);
    fitTable(modal);
    updateScrollFade(modal);
  }

  function sync(modal) {
    var open = isOpen(modal);
    modal.classList.toggle(OPEN_CLASS, open);

    if (open && !wasOpen) layout(modal);

    wasOpen = open;
  }

  function attach(modal) {
    if (attached === modal) return;
    attached = modal;
    sync(modal);

    var styleObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) sync(mutations[i].target);
    });
    styleObserver.observe(modal, { attributes: true, attributeFilter: ['style'] });
  }

  var bodyObserver = new MutationObserver(function () {
    if (attached) return;
    var modal = document.querySelector('.scr-modal');
    if (modal) attach(modal);
  });

  bodyObserver.observe(document.documentElement, { childList: true, subtree: true });

  var existing = document.querySelector('.scr-modal');
  if (existing) attach(existing);

  window.addEventListener('resize', function () {
    if (!attached || !wasOpen) return;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      position(attached);
      fitTable(attached);
      updateScrollFade(attached);
    }, 120);
  });

  /* =========================================================
     UNIT TOGGLE - injected once per modal, real per-product data
     ========================================================= */

  /**
   * Every text node under the footnote that names the unit, with the string
   * it started as.
   *
   * This used to read and rewrite `.bottom-description`.textContent as one
   * string. Assigning textContent replaces the whole subtree with a single
   * text node, so the first switch to CM destroyed the "How to measure"
   * diagram the merchant put there - and switching back to IN restored only
   * the flattened text, never the image. Seven of the sixteen charts on this
   * store carry an <img> in descriptionBottom (and <h5>/<strong> around it),
   * so rewriting has to happen node by node and leave the structure alone.
   * @param {HTMLElement|null} footnote
   * @returns {{node: Text, original: string}[]}
   */
  function collectUnitText(footnote) {
    var entries = [];
    if (!footnote) return entries;

    /* The whole footnote's text is assembled first because the merchant's
       editor splits sentences across text nodes at will - the sweatshirt
       chart's "choose a size 2-3 inches bigger" arrives as " 2-3" and
       " inches bigger" in two separate nodes - so the quantity test below
       can't be answered from inside one node. */
    var walker = document.createTreeWalker(footnote, NodeFilter.SHOW_TEXT, null, false);
    var nodes = [];
    var full = '';
    var node;
    while ((node = walker.nextNode())) {
      nodes.push({ node: node, original: node.nodeValue, start: full.length });
      full += node.nodeValue;
    }

    nodes.forEach(function (entry) {
      if (!/\binch(es)?\b/i.test(entry.original)) return;
      entry.converted = toCentimetres(entry.original, full, entry.start);
      if (entry.converted !== entry.original) entries.push(entry);
    });

    return entries;
  }

  /**
   * @param {string} text one text node's value
   * @param {string} full the whole footnote's text
   * @param {number} start where `text` begins inside `full`
   * @returns {string}
   */
  function toCentimetres(text, full, start) {
    return text.replace(/\b(inches|inch)\b/gi, function (unit, _group, index) {
      /* A quantity in the merchant's own prose - "choose a size 2-3 inches
         bigger than your body chest" - is a real measurement, not a label for
         the table's units. This toggle converts the table, not that sentence,
         so relabelling it would make the advice wrong by a factor of 2.54. */
      var before = full.slice(0, start + index).replace(/[\s ]+$/, '');
      if (/\d$/.test(before)) return unit;

      var word = unit.toLowerCase() === 'inch' ? 'centimeter' : 'centimeters';
      if (unit === unit.toUpperCase()) return word.toUpperCase();
      if (unit.charAt(0) === unit.charAt(0).toUpperCase()) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    });
  }

  /**
   * @param {number} inches
   * @returns {string}
   */
  function inchesToCm(inches) {
    var rounded = Math.round(inches * 2.54 * 10) / 10;
    var whole = Math.round(rounded);
    return whole === rounded ? String(whole) : String(rounded);
  }

  /**
   * @param {HTMLElement} modal
   */
  function buildUnitToggle(modal) {
    if (modal.querySelector('.yg-sg-unit-toggle')) return;

    var table = modal.querySelector('.scr-table');
    if (!table) return;

    /* Header row cells are <th>, never <td>, so this naturally skips the
       S/M/L/XL/XXL header regardless of whether the table has a real
       <thead> - only measurement cells match. */
    var cells = table.querySelectorAll('td:not(:first-child)');
    if (!cells.length) return;

    var any = false;
    cells.forEach(function (cell) {
      var raw = cell.textContent.trim();
      var value = parseFloat(raw);
      if (!raw || isNaN(value)) return;
      cell.setAttribute('data-in', raw);
      cell.setAttribute('data-cm', inchesToCm(value));
      any = true;
    });
    if (!any) return;

    var footnoteText = collectUnitText(modal.querySelector('.bottom-description'));

    var toggle = document.createElement('div');
    toggle.className = 'yg-sg-unit-toggle';
    toggle.setAttribute('role', 'group');
    toggle.setAttribute('aria-label', 'Measurement unit');

    ['in', 'cm'].forEach(function (unit) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'yg-sg-unit-btn' + (unit === 'in' ? ' is-active' : '');
      button.dataset.unit = unit;
      button.setAttribute('aria-pressed', unit === 'in' ? 'true' : 'false');
      button.textContent = unit.toUpperCase();

      button.addEventListener('click', function () {
        toggle.querySelectorAll('.yg-sg-unit-btn').forEach(function (other) {
          var active = other === button;
          other.classList.toggle('is-active', active);
          other.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        cells.forEach(function (cell) {
          var value = cell.getAttribute('data-' + unit);
          if (value != null) cell.textContent = value;
        });

        footnoteText.forEach(function (entry) {
          entry.node.nodeValue = unit === 'cm' ? entry.converted : entry.original;
        });

        fitTable(modal);
        updateScrollFade(modal);
      });

      toggle.appendChild(button);
    });

    table.parentNode.insertBefore(toggle, table);
  }
})();
